import React, {useEffect, useRef, useState} from "react";
import { ComboBoxControlType } from '../Framework/Components/Forms/ComboBox/ComboBoxTypes';
import useComboBox from '../Framework/Components/Forms/ComboBox/useComboBox';
import useFilterSelectOptions from '../Framework/Hooks/useFilterSelectOptions';
import useKeyboardNavigation from '../Framework/Hooks/useKeyboardNavigation';
import { GetObjectIndexByKeyValue } from '../Framework/Constants/Helpers';
import { useOutsiderDetector } from '../Framework/Hooks/useOutsiderDetector';
import ComboBoxFilter from '../Framework/Components/Forms/ComboBox/Components/ComboBoxFilter';
import ComboBoxNoResults from '../Framework/Components/Forms/ComboBox/Components/ComboBoxNoResults';
import Button from '../Framework/Components/Buttons/Button';
import ComboBoxListActions from '../Framework/Components/Forms/ComboBox/Components/ComboBoxListActions';
import ComboBoxItemToggle from '../Framework/Components/Forms/ComboBox/Components/ComboBoxItemToggle';
import InfiniteLoadTrigger from '../Framework/Components/InfiniteLoadTrigger';
import { SelectItemType } from '../Framework/Types/FormTypes';
import { ColourMod } from '../Framework/Data/Enums/LayoutEnums';
import { useController, useFormContext } from 'react-hook-form';
import { ArraysAreEqual } from '../Framework/Constants/DataHelpers';

interface InfiniteDataComboBoxType extends ComboBoxControlType {
	isFetchingNextPage?: boolean;
	fetchNextPage?: any;
	hasNextPage?: boolean;
	itemCounts?: any;
}

const InfiniteDataComboBox = (props: InfiniteDataComboBoxType) => {
	const { control, watch } = useFormContext();

	const [ preSelectedValues, setPreSelectedValues ] = useState<any>(props.defaultValues || []);

	const {
		field,
		fieldState,
	} = useController({
		control,
		name: props.name,
		defaultValue: props.defaultValues,
		rules: {
			required: props.required ? `${props.label} is required` : false,
			validate: (e) => props.required ? e.length > 0 ? true : `${props.label} is required` : true
		},
	});

	const [ isMinimal ] = useState<boolean>(props.minimal || false);
	const [ listAlwaysVisible ] = useState<boolean>(props.listAlwaysVisible || false);

	const [ active, setActive ] = useState<boolean>(listAlwaysVisible || false);
	const [displayValue, setDisplayValue] = useState<string>('');

	const multiSelectRef = useRef<HTMLDivElement>(null);
	const filterRef = useRef<HTMLInputElement>(null);
	const tagListRef = useRef<any>();

	const {
		allSelected,
		selectedIds,
		selectedIdsRef,
		ToggleSingle,
		ToggleAll,
		ResetSelection
	} = useComboBox(props.data, 'value', 'multiSelect', props.valueType, preSelectedValues);

	const {
		filterSelectValue,
		filterSelectItems,
		uniqueFilterSelectValue,
		FilterSelectList,
		ResetFilterSelectOptions,
		HandleFilterSelectBlur,
		HandleFilterSelectFocus
	} = useFilterSelectOptions({ filterData: props.data, exclusionList: selectedIdsRef.current });

	const {
		HandleElementTransitionNavigation,
		HandleKeyboardElementNavigation,
		optionsCycled,
		ResetKeyboardTracking,
	} = useKeyboardNavigation({
		itemClassName: 'checkbox',
		itemContainerClassSelector: '.multi-select-active',
		skipClassName: 'multi-select-actions',
		resetCallback: ResetFilterSelectOptions,
	});

	const UpdateListVisibility = (state: boolean) => {
		setActive(listAlwaysVisible ?? state);
	};

	const ForwardChanges = (data: any) => {
		field.onChange(data);
	}

	const CloseOptions = () => {
		UpdateDisplayValue();
		UpdateListVisibility(false);
		ResetKeyboardTracking();
	};

	const HandleSelection = (e: any, item: any) => {
		const entry = item || {
			label: e?.target.getAttribute('title'),
			value: parseInt(e?.target.getAttribute('data-value'), 10),
		};

		if (e.code && (e.code === 'ArrowDown' || e.code === 'ArrowUp')) {
			HandleKeyboardElementNavigation(e);
		} else if (!e.code) {
			ForwardChanges(ToggleSingle(entry.value, e.target.checked));
		}
	}

	const UpdateDisplayValue = () => {
		let formatDisplayValue = selectedIdsRef.current.length > 0 ? selectedIdsRef.current.length === 1 ? `${selectedIdsRef.current.length} Item Selected` : `${selectedIdsRef.current.length} Items Selected` : '';

		if(selectedIdsRef.current.length === 1) {
			const value = GetObjectIndexByKeyValue(props.data, 'value', selectedIdsRef.current[0]);

			if(value !== -1) {
				formatDisplayValue = props.data[value].label;
			}
		}

		setDisplayValue(selectedIdsRef.current.length > 0 ? active ? filterSelectValue : formatDisplayValue : '');
	}

	const HandleFilterSelectBlurCallback = (e: any) => {
		if (active) {
			setActive(listAlwaysVisible ?? false);
		}
	}

	const HandleFilterSelectFocusCallback = () => {
		setActive(true);
	}

	const HandleCreateCallback = () => {
		props.createCallback(filterSelectValue);
		ResetFilterSelectOptions();
	}

	useOutsiderDetector(multiSelectRef, () => active ? CloseOptions() : null);

	useEffect(() => {
		if (optionsCycled) {
			CloseOptions();
		}
	}, [optionsCycled]);

	useEffect(() => {
		UpdateDisplayValue();
	}, [active, selectedIds]);

	useEffect(() => {
		if(filterRef.current) {
			filterRef.current.value = displayValue
		}
	}, [displayValue]);

	useEffect(() => {
		if(props.defaultValues) {
			setPreSelectedValues(props.defaultValues);
		} else {
			ResetSelection();
		}
		ResetFilterSelectOptions();
	}, [props.reset]);

	useEffect(() => {
		const subscription = watch((e) => {
			if(e[props.name] && !ArraysAreEqual(e[props.name], selectedIds)) {
				setPreSelectedValues(e[props.name]);
			}
		});
		return () => subscription.unsubscribe();
	}, [watch]);

	return (
		<div className={'flex-column multi-select-container flex-gap-lg'} ref={multiSelectRef}>
			<div className={listAlwaysVisible && !isMinimal ? 'multi-select-inline-control' : ''}>

				<ComboBoxFilter
					filterRef={filterRef}
					filterSelectValue={filterSelectValue}
					placeholder={props.placeholder}
					label={props.label ?? 'ComboBox'}
					resetCallback={() => ResetFilterSelectOptions()}
					blurCallback={(e: any) => HandleFilterSelectBlur(e, HandleFilterSelectBlurCallback)}
					filterActionCallback={(e: any) => FilterSelectList(e)}
					filterNavigationCallback={(e: any) => HandleElementTransitionNavigation(e)}
					filterFocusCallback={(e: any) => HandleFilterSelectFocus(e, HandleFilterSelectFocusCallback)}
					minimal={isMinimal}
					errorMessage={fieldState.error?.message as string}
					isValid={!fieldState.error ? true : null}
					isActive={active}
					isLoading={props.isLoading}
					isDisabled={props.isDisabled}
				/>

				<div ref={tagListRef} className={`${listAlwaysVisible && !isMinimal ? 'multi-select-inline-list' : 'generic-menu-container'} ${isMinimal ? 'in-dropdown-menu' : ''} multi-select-entries ${active ? 'multi-select-active' : ''}`} style={{ maxHeight: '300px', overflow: 'auto'}} tabIndex={-1}>
					{filterSelectItems && filterSelectItems.length === 0 && !props.createCallback && (
						<ComboBoxNoResults />
					)}

					{filterSelectValue !== '' && uniqueFilterSelectValue && props.createCallback && (
						<div className={'p-sm'}>
							<Button title={`${props.createLabel ? props.createLabel : 'Create'} '${filterSelectValue}'`} fullWidth={true} onClick={() => HandleCreateCallback()} colour={ColourMod.Primary} />
						</div>
					)}

					{filterSelectValue === '' && filterSelectItems.length > 0 && (
						<ComboBoxListActions allSelected={allSelected} toggleCallBack={(e: boolean) => ForwardChanges(ToggleAll(e))} />
					)}

					{filterSelectItems && filterSelectItems.length > 0 && filterSelectItems.map((item: SelectItemType, index: number) => {
						if (item.value === undefined || item.value === '') return null;

						return (
							<ComboBoxItemToggle
								key={`comboItem${props.label}${item.value}`}
								name={`${props.name}${index}`}
								label={props.label}
								item={item}
								selectedValues={selectedIds}
								indeterminateValues={props.indeterminateValues}
								onChangeCallback={(e: any) => HandleSelection(e, item)}
								onKeyDownCallback={(e: any) => HandleKeyboardElementNavigation(e)}
							/>
						);
					})}

					<InfiniteLoadTrigger
						ref={tagListRef.current}
						loaded={props.itemCounts.loaded}
						totalCount={props.itemCounts.total}
						hasNextPage={props.hasNextPage}
						fetchNextPage={props.fetchNextPage}
						isFetchingNextPage={props.isFetchingNextPage}
					/>

				</div>

			</div>
		</div>
	)
};

export default InfiniteDataComboBox
